import type { UploadFile } from "antd";
import { message } from "antd";
import axios from "axios";
import { useCallback, useState } from "react";
import { v4 as uuidv4 } from "uuid";

const UPLOAD_FILE_URL = "https://viagsapi.azurewebsites.net/api/uploadfile/upload-file";

export interface UploadFileOptions {
  onSuccess?: (urls: string[]) => void;
  onError?: (error: any) => void;
}

export interface UploadFileResponse {
  statusCode: number;
  data: string[];
}

/**
 * Hook để upload file lên server
 * @returns {Object} Object chứa hàm uploadFile và trạng thái isUploading
 *
 * @example
 * ```tsx
 * const { uploadFile, isUploading } = useUploadFile();
 *
 * // Upload từ File[]
 * const urls = await uploadFile([file1, file2]);
 *
 * // Upload từ Ant Design Upload fileList
 * const fileList = form.getFieldValue('attachments');
 * const filesToUpload = fileList
 *   .filter((file) => file.originFileObj)
 *   .map((file) => file.originFileObj);
 * const urls = await uploadFile(filesToUpload);
 * ```
 */
const useUploadFile = () => {
  const [isUploading, setIsUploading] = useState(false);

  const uploadFile = useCallback(
    async (files: File[] | UploadFile[], options?: UploadFileOptions): Promise<string[]> => {
      if (!files || files.length === 0) {
        return [];
      }

      // Xử lý fileList từ Ant Design Upload (có originFileObj)
      const filesToUpload: File[] = files
        .map((file) => {
          // Nếu là UploadFile từ Ant Design, lấy originFileObj
          if (file && typeof file === "object" && "originFileObj" in file) {
            return file.originFileObj as File;
          }
          // Nếu là File trực tiếp
          return file as File;
        })
        .filter((file): file is File => file instanceof File);

      if (filesToUpload.length === 0) {
        // Nếu không có file mới, kiểm tra xem có URL đã có sẵn không (từ fileList của Ant Design)
        const existingUrls = (files as UploadFile[])
          .filter(
            (file): file is UploadFile =>
              file && typeof file === "object" && "url" in file && !("originFileObj" in file),
          )
          .map((file) => file.url)
          .filter((url): url is string => !!url);
        return existingUrls;
      }

      setIsUploading(true);

      try {
        const formData = new FormData();
        filesToUpload.forEach((file) => {
          const customFileName = `${uuidv4()}_${file.name}`;
          formData.append("files", file, customFileName);
        });

        const response = await axios.post<UploadFileResponse>(UPLOAD_FILE_URL, formData);

        setIsUploading(false);

        if (response.data.statusCode === 200) {
          const uploadedUrls = response?.data?.data || [];

          // Kết hợp với các file đã upload trước đó (nếu có)
          const existingUrls = (files as UploadFile[])
            .filter(
              (file): file is UploadFile =>
                file && typeof file === "object" && "url" in file && !("originFileObj" in file),
            )
            .map((file) => file.url)
            .filter((url): url is string => !!url);

          const allUrls = [...existingUrls, ...uploadedUrls];
          options?.onSuccess?.(allUrls);
          return allUrls;
        } else {
          const errorMessage =
            "Định dạng này không hợp lệ, vui lòng chọn các định dạng khác (.pdf,.docx,.doc,.pptx,.ppt,.xlsx,.xls,.csv,.png,.jpg,.jpeg)";
          message.error(errorMessage);
          const error = new Error(errorMessage);
          options?.onError?.(error);
          throw error;
        }
      } catch (error) {
        setIsUploading(false);
        console.error("Upload file error:", error);
        message.error("Upload file thất bại");
        options?.onError?.(error);
        throw error;
      }
    },
    [],
  );

  return {
    uploadFile,
    isUploading,
  };
};

export default useUploadFile;
